/**
 * PRIV-1 third-party egress consent modal. Shown the first time a user
 * triggers an action that sends their data to a named third party (Exa search,
 * Gemini image). Accepting records consent server-side; the action then
 * proceeds. Escape or a backdrop click cancels (treated as decline).
 *
 * Presentational only — the decision logic + copy live in `../consentModel`.
 */
import { type Component, Show, onCleanup, onMount } from "solid-js";

import { providerDisclosure } from "../consentModel";

interface Props {
  /** Provider id to consent to, or `null` when the modal is hidden. */
  provider: string | null;
  /** User accepted — caller records consent then proceeds. */
  onAccept: () => void;
  /** User declined (button, Escape, or backdrop) — caller aborts the action. */
  onCancel: () => void;
}

const ConsentModal: Component<Props> = (props) => {
  onMount(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!props.provider) return;
      if (e.key === "Escape") {
        e.preventDefault();
        props.onCancel();
      }
    };
    window.addEventListener("keydown", onKey);
    onCleanup(() => window.removeEventListener("keydown", onKey));
  });

  return (
    <Show when={props.provider}>
      {(provider) => {
        const disclosure = () => providerDisclosure(provider());
        return (
          <div
            class="cmd-palette-backdrop"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) props.onCancel();
            }}
          >
            <section
              class="consent-modal"
              aria-label={disclosure()?.title ?? "third-party access"}
            >
              <h2 class="consent-modal-title">
                {disclosure()?.title ?? "Allow third-party access?"}
              </h2>
              <p class="consent-modal-body">
                {disclosure()?.body ??
                  "This action sends your data to a third-party provider."}
              </p>
              <div class="consent-modal-actions">
                <button
                  type="button"
                  class="consent-btn-cancel"
                  onClick={() => props.onCancel()}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="consent-btn-allow"
                  onClick={() => props.onAccept()}
                >
                  {disclosure()?.allowLabel ?? "Allow"}
                </button>
              </div>
            </section>
          </div>
        );
      }}
    </Show>
  );
};

export default ConsentModal;
